import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useThreadsAuth } from '../../context/ThreadsAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// ─── Login Button ─────────────────────────────────────────────────────────────

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #fff;
  background: linear-gradient(135deg, #000 0%, #1a1a1a 50%, #000 100%);
  background-size: 200% auto;
  border: 1.5px solid rgba(255, 255, 255, 0.15);
  transition: border-color 0.25s ease, transform 0.15s ease, box-shadow 0.25s ease;

  &:hover {
    animation: ${shimmer} 1.5s linear infinite;
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

// ─── Modal Overlay ────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Modal = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px;
  width: 100%;
  max-width: 480px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalDesc = styled.p`
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 20px;
  line-height: 1.55;

  a {
    color: #fe88f1;
    text-decoration: underline;
  }
`;

const Steps = styled.ol`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 20px;
  padding-left: 20px;
  line-height: 1.8;

  a { color: #fe88f1; }
`;

const TokenInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #fff;
  font-size: 0.9rem;
  font-family: monospace;
  box-sizing: border-box;
  transition: border-color 0.2s;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: rgba(254, 136, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
    font-family: inherit;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const ConfirmBtn = styled.button`
  flex: 1;
  padding: 11px;
  background: #fe88f1;
  color: #111;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const CancelBtn = styled.button`
  padding: 11px 18px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: rgba(255, 255, 255, 0.1); }
`;

const ErrMsg = styled.p`
  font-size: 0.8rem;
  color: #ff7e7e;
  margin: 6px 0 0;
`;

// ─── Logged-in User Badge ─────────────────────────────────────────────────────

const UserBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
`;

const AvatarFallback = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: #fe88f1;
`;

const UserName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
`;

const LogoutBtn = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

// ─── Threads Icon ─────────────────────────────────────────────────────────────

const ThreadsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 192 192" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="#fff" d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.11 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.14-23.82 1.371-39.134 15.264-38.105 34.568.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.414 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376C123.533 160.059 108.976 166 92 166c-19.054 0-34.929-6.585-47.17-19.57C32.612 133.451 26.5 115.3 26.5 92c0-23.3 6.112-41.451 18.33-54.43C56.071 24.585 71.946 18 91 18c19.204 0 34.428 6.006 45.22 17.856 4.71 5.12 8.296 11.246 10.694 18.258l15.576-4.662c-3.037-9.026-7.697-16.935-13.938-23.48C132.956 10.513 113.828 2 91 2 67.948 2 48.91 9.916 35.17 25.016 23.086 38.378 16.5 57.23 16.5 92s6.586 53.622 18.67 66.984C48.91 174.084 67.948 182 91 182h.959c20.075-.457 37.317-7.325 50.088-20.115 17.45-17.468 16.955-39.234 11.177-52.705-4.122-9.61-11.997-17.396-11.687-20.192Z" />
    <path fill="#fff" d="M88.763 130.5c-10.375 0-16.728-4.606-17.08-12.3-.176-3.867 1.48-7.4 4.67-9.95 3.595-2.87 8.973-4.397 15.543-4.413 5.413-.015 10.576.526 15.382 1.597-.672 15.037-7.742 25.066-18.515 25.066Z" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const ThreadsLoginButton = ({ returnTo }) => {
  const { isLoggedIn, isLoading, user, logout } = useThreadsAuth();

  const handleLogin = () => {
    // Save the page to return to after OAuth completes
    const destination = returnTo || window.location.pathname;
    sessionStorage.setItem('threads_return_to', destination);
    // Redirect to backend OAuth entry point
    window.location.href = `${BACKEND_URL}/auth/threads`;
  };

  // Don't render anything while the startup token-validation check is in flight
  if (isLoading) return null;

  // Logged-in state — show avatar badge
  if (isLoggedIn && user) {
    return (
      <UserBadge>
        {user.profilePicUrl ? (
          <Avatar src={user.profilePicUrl} alt={user.name || user.username} />
        ) : (
          <AvatarFallback>
            {(user.name || user.username || '?')[0].toUpperCase()}
          </AvatarFallback>
        )}
        <UserName>@{user.username || user.name}</UserName>
        <LogoutBtn onClick={logout}>Sign out</LogoutBtn>
      </UserBadge>
    );
  }

  return (
    <Button onClick={handleLogin} type="button">
      <ThreadsIcon />
      Login with Threads
    </Button>
  );
};

export default ThreadsLoginButton;
